import React, { memo, useCallback, useMemo, useRef } from 'react';
import classNames from 'classnames';
import { Icon } from '@Sage/hoi-poi-ui';
import { SectionListCrud } from '@Sage/ssm-web5';

import { getLiteral } from 'utils/getLiteral';
import { formatDecimalByConfig, DECIMAL_TYPES, normalizeDecimalValue } from 'utils/decimal';

import CONFIG from './TableConfig';

import './styles.scss';

// Helper to parse discount values
const parseDiscount = (value) => {
    const parsed = normalizeDecimalValue(value);
    return parsed == null ? 0 : parsed;
};

// Helper to compute total discount from individual discounts
const computeTotalDiscount = (d1, d2, d3, d4) => {
    const factor = (1 - d1 / 100) * (1 - d2 / 100) * (1 - d3 / 100) * (1 - d4 / 100);
    return (1 - factor) * 100;
};

// Helper to format total discount for display
const formatTotalDiscount = ({ value, idEnvironment, idCurrency }) => {
    const formatted = formatDecimalByConfig({
        value,
        type: DECIMAL_TYPES.GENERAL,
        idEnvironment,
        idCurrency,
        fieldId: 'totalDiscount',
    });
    return formatted ? `${formatted}%` : '-';
};

// CRUD Schema for editable percentage fields
const crudSchema = [
    {
        defaultValue: 0,
        id: 'discount1',
        mandatory: false,
        postComponent: <span>%</span>,
        type: 'percentage',
    },
    {
        defaultValue: 0,
        id: 'discount2',
        mandatory: false,
        postComponent: <span>%</span>,
        type: 'percentage',
    },
    {
        defaultValue: 0,
        id: 'discount3',
        mandatory: false,
        postComponent: <span>%</span>,
        type: 'percentage',
    },
    {
        defaultValue: 0,
        id: 'discount4',
        mandatory: false,
        postComponent: <span>%</span>,
        type: 'percentage',
    },
];

const DiscountsTable = memo(({ list, onChange, idEnvironment, idCurrency }) => {
    const tableRef = useRef(null);

    // ========================================
    // DERIVED STATE & MEMOIZATION
    // ========================================
    const rowData = useMemo(() => {
        if (!list || list.length === 0) return [];
        return list.map((item, index) => {
            const d1 = parseDiscount(item.discount1);
            const d2 = parseDiscount(item.discount2);
            const d3 = parseDiscount(item.discount3);
            const d4 = parseDiscount(item.discount4);
            const total = computeTotalDiscount(d1, d2, d3, d4);

            return {
                discount1: d1,
                discount2: d2,
                discount3: d3,
                discount4: d4,
                familyname: item.familyname || '',
                id: item.id,
                numLine: index + 1,
                totalDiscount: formatTotalDiscount({
                    value: total,
                    idEnvironment,
                    idCurrency,
                }),
            };
        });
    }, [idCurrency, idEnvironment, list]);

    // ========================================
    // EVENT HANDLERS & CALLBACKS
    // ========================================
    const handleOnRef = useCallback((ref) => {
        tableRef.current = ref;
    }, []);

    const onChangeCell = useCallback(
        ({ field, rowIndex, value }) => {
            if (!onChange || !list) return;
            const row = list[rowIndex];
            if (!row) return;
            onChange(field, row, rowIndex, value);
        },
        [list, onChange],
    );

    const onDeleteRow = useCallback(
        (row) => {
            if (!onChange) return;
            // Pass null as field to indicate deletion
            const rowIndex = list.findIndex((item) => item.id === row.id);
            onChange(null, row, rowIndex, null);
        },
        [list, onChange],
    );

    const getRowNodeId = useCallback((data) => data.id, []);

    const getColumnDefs = useCallback(
        ({ transformTableToCrud }) => {
            return new Promise((resolve) => {
                const config = CONFIG();
                let columnDefs = config.columnDefs;

                // Transform columns to CRUD mode using the provided function
                columnDefs = transformTableToCrud({
                    columnDefs,
                    crudSchema,
                    inputProps: {
                        onChange: onChangeCell,
                    },
                });

                resolve(columnDefs);
            });
        },
        [onChangeCell],
    );

    const actions = useMemo(
        () => [
            (row) => ({
                icon: <Icon name="close" onClick={() => onDeleteRow(row)} />,
                tooltip: { content: getLiteral('label_remove_from_list') },
            }),
        ],
        [onDeleteRow],
    );

    const { domLayout, isEmpty, isScrollable } = useMemo(() => {
        if (!rowData?.length) {
            return {
                domLayout: 'normal',
                isEmpty: true,
                isScrollable: false,
            };
        } else if (rowData.length <= 5) {
            return {
                domLayout: 'autoHeight',
                isEmpty: false,
                isScrollable: false,
            };
        }
        return {
            domLayout: 'normal',
            isEmpty: false,
            isScrollable: true,
        };
    }, [rowData]);

    // ========================================
    // RENDER
    // ========================================
    if (!rowData || rowData.length === 0) {
        return null;
    }

    return (
        <div className="discounts-table-wrapper">
            <SectionListCrud
                actions={actions}
                className={classNames('discounts-table', {
                    'discounts-table--empty': isEmpty,
                    'discounts-table--scrollable': isScrollable,
                })}
                domLayout={domLayout}
                forceRowUpdating
                getColumnDefs={getColumnDefs}
                getRowNodeId={getRowNodeId}
                id="discounts-table"
                idCurrency={idCurrency}
                idEnvironment={idEnvironment}
                onRef={handleOnRef}
                rowData={rowData}
                useSort={false}
            />
        </div>
    );
});

DiscountsTable.displayName = 'DiscountsTable';

export default DiscountsTable;
